import { App, isTiergruppeActive } from '@ps20-frontend/frontend-models';
import { isOwnedByTgv } from '@ps20-frontend/shop-integration-redux';
import { IAppState } from '../appState';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import {
  getRabattstufe,
  getRbdString,
  getTbString,
  getVrString,
  ProduktForTabelle,
} from './ausgewaehlte-produkte.selector';
import {
  THREE_YEARS_LAUFZEIT,
  isVertragslaufzeitChangeForbidden,
} from './vertragslaufzeit.selector';
import { angebotIsTarifiert } from './warenkorb.selector';

export interface TiergruppeWarenkorb {
  tgvRow: WarenkorbAngebot;
  subAngebote: Array<ProduktForTabelle>;
  monthlyTotal: number;
}

export const SUB_ANGEBOT_ORDER: Array<App> = [App.TKV_V2, App.THH_HUND, App.THH_PFERD];

export { isOwnedByTgv };

export const hasUncalculatedOffer = (warenkorb: Array<WarenkorbAngebot>): boolean => {
  const someOfferNotCalculated = warenkorb
    .filter(produkt => produkt.appId !== App.TGV)
    .some(produkt => produkt.ausgewaehlteLinie == null);
  const tiergruppeWithoutSubAngebot =
    warenkorb.some(produkt => produkt.appId === App.TGV) &&
    !warenkorb.some(produkt => isOwnedByTgv(produkt) || SUB_ANGEBOT_ORDER.includes(produkt.appId));
  const hasOpenTgvPlaceholder = warenkorb.some(
    produkt => produkt.appId === App.TGV && produkt.tgvPlaceholder === true,
  );
  return someOfferNotCalculated || tiergruppeWithoutSubAngebot || hasOpenTgvPlaceholder;
};

const toProduktForTabelle = (produkt: WarenkorbAngebot): ProduktForTabelle =>
  ({
    produkt,
    appId: produkt.appId,
    rbdInfo: getRbdString(produkt),
    tbInfo: getTbString(produkt),
    vrInfo: getVrString(produkt),
    kombirabatt: getRabattstufe(produkt),
  }) as ProduktForTabelle;

const orderRank = (produkt: WarenkorbAngebot): number => {
  const rank = SUB_ANGEBOT_ORDER.indexOf(produkt.appId);
  return rank === -1 ? SUB_ANGEBOT_ORDER.length : rank;
};

const computeTiergruppeWarenkorb = (
  warenkorb: Array<WarenkorbAngebot>,
): TiergruppeWarenkorb | null => {
  const tgvRow = warenkorb.find(produkt => produkt.appId === App.TGV);
  if (!tgvRow) {
    return null;
  }
  const ownedRows = warenkorb.filter(isOwnedByTgv);
  if (ownedRows.length === 0) {
    return null;
  }
  const orderedRows = ownedRows
    .map((produkt, index) => ({ produkt, index }))
    .sort((a, b) => orderRank(a.produkt) - orderRank(b.produkt) || a.index - b.index)
    .map(entry => entry.produkt);
  const monthlyTotal = ownedRows
    .filter(angebotIsTarifiert)
    .reduce((sum, produkt) => sum + (produkt.bruttopreis ?? 0), 0);
  return {
    tgvRow,
    subAngebote: orderedRows.map(toProduktForTabelle),
    monthlyTotal,
  };
};

let cachedWarenkorb: Array<WarenkorbAngebot> | null = null;
let cachedResult: TiergruppeWarenkorb | null = null;

export const selectTiergruppeWarenkorb = (state: IAppState): TiergruppeWarenkorb | null => {
  if (!isTiergruppeActive(state.transientState?.featureToggles)) {
    return null;
  }
  if (cachedWarenkorb !== state.warenkorb) {
    cachedResult = computeTiergruppeWarenkorb(state.warenkorb);
    cachedWarenkorb = state.warenkorb;
  }
  return cachedResult;
};

let cachedEmptyGroup: TiergruppeWarenkorb | null = null;

export const selectTgvRow = (state: IAppState): WarenkorbAngebot | undefined =>
  state.warenkorb?.find(produkt => produkt.appId === App.TGV);

export const selectTgvGroup = (state: IAppState): TiergruppeWarenkorb | null => {
  const gruppe = selectTiergruppeWarenkorb(state);
  if (gruppe) {
    return gruppe;
  }
  if (!isTiergruppeActive(state.transientState?.featureToggles)) {
    return null;
  }
  const tgvRow = selectTgvRow(state);
  if (!tgvRow) {
    return null;
  }
  if (cachedEmptyGroup?.tgvRow !== tgvRow) {
    cachedEmptyGroup = { tgvRow, subAngebote: [], monthlyTotal: 0 };
  }
  return cachedEmptyGroup;
};

export const selectIsTgvGroupIncomplete = (state: IAppState): boolean => {
  if (!isTiergruppeActive(state.transientState?.featureToggles)) {
    return false;
  }
  const tgvRow = selectTgvRow(state);
  if (!tgvRow) {
    return false;
  }
  return (
    tgvRow.tgvPlaceholder === true ||
    !state.warenkorb.some(produkt => isOwnedByTgv(produkt) && angebotIsTarifiert(produkt))
  );
};

export const selectHasOwnedRowsWithoutTgvRow = (state: IAppState): boolean =>
  isTiergruppeActive(state.transientState?.featureToggles) &&
  !selectTgvRow(state) &&
  (state.warenkorb ?? []).some(isOwnedByTgv);

export const selectTgvChildrenWithChangeableLaufzeit = (
  state: IAppState,
): Array<WarenkorbAngebot> =>
  (selectTiergruppeWarenkorb(state)?.subAngebote ?? [])
    .map(subAngebot => subAngebot.produkt)
    .filter(
      kind =>
        kind.laufzeit === THREE_YEARS_LAUFZEIT &&
        kind.ausgewaehlteLinie != null &&
        !isVertragslaufzeitChangeForbidden(kind, state.transientState?.featureToggles),
    );

export const selectTgvHasChildrenWithChangeableLaufzeit = (state: IAppState): boolean =>
  selectTgvChildrenWithChangeableLaufzeit(state).length > 0;
